import React from "react";
import { Form, Input } from "antd";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { generateForgotPassword } from "src/services/authService";
import Notification from "src/util/libs/notification";

interface ForgotPasswordFormValues {
  email: string;
}

interface ForgotPasswordProps {
  isForgetScreen: boolean;
  setIsForgetScreen: (value: boolean) => void;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({ isForgetScreen, setIsForgetScreen }) => {
  const [form] = Form.useForm<ForgotPasswordFormValues>();

  const { mutate, isPending } = useMutation({
    mutationFn: (values: ForgotPasswordFormValues) =>
      generateForgotPassword({ email: values.email } as Record<string, unknown>),
    onSuccess: () => {
      Notification.success({
        message: "Reset link sent",
        description: "If an account exists for this email, a reset link has been sent.",
      });
    },
    onError: () => {
      Notification.error({
        message: "Reset failed",
        description: "Unable to send reset link. Please try again later.",
      });
    },
  });

  if (!isForgetScreen) return null;

  const handleFinish = (values: ForgotPasswordFormValues) => {
    mutate(values);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-center">
        <h2 className="mb-1 text-[1.25rem] font-semibold text-[#001b3a]">Forgot Password</h2>
        <p className="m-0 text-[0.875rem] text-[#485056]">
          Enter your email address and we&apos;ll send you instructions to reset your password.
        </p>
      </div>

      <Form<ForgotPasswordFormValues> layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please input your email!" },
            { type: "email", message: "Please enter a valid email!" },
          ]}
        >
          <Input placeholder="Enter your email" />
        </Form.Item>

        <Form.Item className="!mb-2">
          <button
            type="submit"
            className="w-full rounded-md bg-[#001b3a] px-4 py-3 text-[0.9375rem] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isPending}
          >
            {isPending ? "Sending..." : "Send Reset Link"}
          </button>
        </Form.Item>
      </Form>

      <div className="text-center">
        <Link
          to="#"
          className="text-[14px] !text-[#000000e0]"
          onClick={(event) => {
            event.preventDefault();
            setIsForgetScreen(false);
          }}
        >
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;

